import PropTypes from 'prop-types';
import React from 'react';
import styled, { useTheme } from 'styled-components';
import ButtonV2 from 'components/ButtonV2';
import {
  Icon,
  PlainButton,
  SkeletonBar,
  inkA,
  orange,
  tokens,
} from 'components/v3';
import { displayAnswer } from '../shared/answerFormat';
import { formatDateTime } from '../shared/dates';
import { submitterEmail, submitterLabel } from '../shared/submitter';
import { gridRows } from './entry/entryExport';
import { isAgreement } from './entry/entryModel';
import Glyph from './Glyph';
import { sx } from './sx';
import { rowField } from './versionFields';

// Entries table (build plan §5.7, T28). The columns are the active saved
// view's (views/viewModel colDef): entry details (_id, _date, _by, _email)
// or answer fields, each with its own width. Sorting runs on the server;
// each sortable header is a PlainButton inside a columnheader that carries
// aria-sort (the account email can't be sorted). Each row's Entry ID is a
// PlainButton stretched over the whole row, so clicking anywhere on the row
// (or Enter on the button) opens the entry; entries your team edited carry
// a small pen. The table never squeezes a column below its width: when the
// card is narrower it scrolls sideways inside its card. The empty, no-match
// and "couldn't load" notices sit under the scroller so they stay in view.
// `header` (the saved-view tabs) sits above the column headers.

export const DEFAULT_SORT = { key: 'submitted_at', direction: 'desc' };

// Clicking the active column flips it; a new column starts newest-first for
// Submitted and A→Z for everything else.
export const nextSort = (current, key) => {
  if (current.key === key) {
    return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
  }
  return { key, direction: key === 'submitted_at' ? 'desc' : 'asc' };
};

const COLUMN_GAP = 12;
const ROW_PAD_X = 16;

export const gridTemplateOf = (cols) =>
  cols.map((c) => `minmax(${c.w}px, ${c.w}fr)`).join(' ');

// The narrowest the table can be: every column at its width, plus the gaps
// between them and the row padding.
export const tableMinWidth = (cols) =>
  cols.reduce((sum, c) => sum + c.w, 0) +
  COLUMN_GAP * Math.max(0, cols.length - 1) +
  ROW_PAD_X * 2;

const Grid = styled.div`
  display: grid;
  grid-template-columns: ${(p) => p.$template};
  column-gap: ${COLUMN_GAP}px;
  align-items: center;
  padding: 0 ${ROW_PAD_X}px;
`;

const BodyRow = styled(Grid)`
  position: relative;
  min-height: 48px;
  border-top: 1px solid ${(p) => p.$line};
  background: ${(p) => (p.$active ? orange(0.1) : 'transparent')};
  transition: background 120ms ease;

  &:hover {
    background: ${(p) => (p.$active ? orange(0.1) : orange(0.05))};
  }
`;

// The row's one control. Its ::after covers the row, so the whole row is
// the click target, and the focus ring outlines the row.
const RowButton = styled(PlainButton)`
  font-size: 12.5px;
  font-weight: 700;
  letter-spacing: 0.2px;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    border-radius: 2px;
  }

  &:focus-visible {
    box-shadow: none;
  }

  &:focus-visible::after {
    box-shadow: inset 0 0 0 2px ${orange(0.6)};
  }
`;

const EditedMark = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  margin-left: 7px;
  border-radius: 5px;
  vertical-align: -3px;
  background: ${(p) => sx(p.theme).tint};
  color: ${(p) => sx(p.theme).otext};
`;

const AgreedText = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-weight: 600;
  color: ${(p) => sx(p.theme).ok};
`;

const SortButton = styled(PlainButton)`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 100%;
  padding: 12px 0;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  color: ${(p) => (p.$active ? orange(1) : p.$color)};

  &:hover {
    color: ${(p) => (p.$active ? orange(1) : p.$hover)};
  }
`;

const ellipsis = {
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const ariaSort = (col, sort) => {
  if (!col.sort) return undefined;
  if (sort.key !== col.sort) return 'none';
  return sort.direction === 'asc' ? 'ascending' : 'descending';
};

const HeaderCell = ({ col, sort, onSort }) => {
  const theme = useTheme();
  const t = tokens(theme);
  const active = !!col.sort && sort.key === col.sort;
  const label = <span style={ellipsis}>{col.label}</span>;
  let glyph = null;
  if (col.sort) {
    glyph = active ? (
      <Icon
        name={sort.direction === 'asc' ? 'chevron-up' : 'chevron-down'}
        size={13}
        color="currentColor"
        strokeWidth={2.5}
      />
    ) : (
      <Icon name="chevrons-up-down" size={12} color={inkA(theme, 0.3)} />
    );
  }
  return (
    <div
      role="columnheader"
      aria-sort={ariaSort(col, sort)}
      style={{
        ...ellipsis,
        textAlign: col.align === 'center' ? 'center' : undefined,
      }}
    >
      {col.sort ? (
        <SortButton
          onClick={() => onSort(col.sort)}
          $active={active}
          $color={t.label}
          $hover={t.text}
          data-tip={`${col.label} · click to sort`}
        >
          {label}
          {glyph}
        </SortButton>
      ) : (
        <span
          style={{
            display: 'block',
            padding: '12px 0',
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: '0.8px',
            textTransform: 'uppercase',
            color: t.label,
          }}
        >
          {col.label}
        </span>
      )}
    </div>
  );
};

HeaderCell.propTypes = {
  col: PropTypes.object.isRequired,
  sort: PropTypes.shape({ key: PropTypes.string, direction: PropTypes.string })
    .isRequired,
  onSort: PropTypes.func.isRequired,
};

// What a cell shows: { text, agreed? }. A repeater shows how many entries
// it has, an agreement shows Agreed.
export const cellContent = (col, row, index) => {
  if (col.key === '_by') return { text: submitterLabel(row) || '—' };
  if (col.key === '_email') return { text: submitterEmail(row) || '—' };
  if (col.key === '_date') return { text: formatDateTime(row.submitted_at) };
  if (col.key === '_id') return { text: row.reference };
  const field = rowField(index, row, col.column);
  if (!field) return { text: '—' };
  const value = (row.answers || {})[col.key];
  if (isAgreement(field)) {
    return value === true ? { text: 'Agreed', agreed: true } : { text: '—' };
  }
  if (field.type === 'repeater') {
    const n = gridRows(field, value).length;
    return { text: n ? `${n} ${n === 1 ? 'row' : 'rows'}` : '—' };
  }
  return { text: displayAnswer(field, value) };
};

const Cell = ({ content, strong, muted, align }) => {
  const t = tokens(useTheme());
  const empty = content.text === '—';
  return (
    <div
      role="cell"
      title={content.text.length > 24 ? content.text : undefined}
      style={{
        ...ellipsis,
        padding: '13px 0',
        fontSize: 14,
        textAlign: align === 'center' ? 'center' : undefined,
        fontWeight: strong && !empty ? 600 : 400,
        color: (() => {
          if (empty || muted) return t.faint;
          return strong ? t.text : t.sub;
        })(),
      }}
    >
      {content.agreed ? (
        <AgreedText>
          <Glyph name="check" size={13} />
          Agreed
        </AgreedText>
      ) : (
        content.text
      )}
    </div>
  );
};

Cell.propTypes = {
  content: PropTypes.shape({ text: PropTypes.string, agreed: PropTypes.bool })
    .isRequired,
  strong: PropTypes.bool,
  muted: PropTypes.bool,
  align: PropTypes.string,
};

Cell.defaultProps = { strong: false, muted: false, align: null };

const Row = ({ row, cols, template, index, active, first, onOpen }) => {
  const t = tokens(useTheme());
  const lead = (cols.find((c) => !c.meta) || {}).key;
  const strongKey = (key, col) =>
    key === lead || key === '_by' || (col.column && col.column.type === 'name');
  const idCol = cols.find((c) => c.key === '_id');
  return (
    <BodyRow
      role="row"
      data-tour={first ? 'submissions-row' : undefined}
      $template={template}
      $line={t.line}
      $active={active}
    >
      {cols.map((col) => {
        if (col.key === '_id' || (!idCol && col === cols[0])) {
          return (
            <div role="cell" key={col.key} style={ellipsis}>
              <RowButton
                aria-label={`Open entry ${row.reference}`}
                onClick={() => onOpen(row)}
                style={{ color: t.sub }}
              >
                {col.key === '_id'
                  ? row.reference
                  : cellContent(col, row, index).text}
              </RowButton>
              {row.edited_at ? (
                <EditedMark
                  role="img"
                  aria-label="Edited by your team"
                  data-help="edited-mark"
                >
                  <Glyph name="pencil" size={11} />
                </EditedMark>
              ) : null}
            </div>
          );
        }
        return (
          <Cell
            key={col.key}
            content={cellContent(col, row, index)}
            strong={strongKey(col.key, col)}
            muted={col.key === '_email'}
            align={col.align}
          />
        );
      })}
    </BodyRow>
  );
};

Row.propTypes = {
  row: PropTypes.object.isRequired,
  cols: PropTypes.arrayOf(PropTypes.object).isRequired,
  template: PropTypes.string.isRequired,
  index: PropTypes.object.isRequired,
  active: PropTypes.bool.isRequired,
  // The first row is the product tour's "Open an entry" target.
  first: PropTypes.bool.isRequired,
  onOpen: PropTypes.func.isRequired,
};

const SkeletonRows = ({ template, count, line }) =>
  Array.from({ length: 5 }, (_, i) => (
    <Grid
      key={i}
      role="row"
      $template={template}
      style={{ minHeight: 48, borderTop: '1px solid ' + line }}
    >
      {Array.from({ length: count }, (__, c) => (
        <div key={c} role="cell">
          <SkeletonBar $w={c === 0 ? '70px' : '70%'} $h="12px" />
        </div>
      ))}
    </Grid>
  ));

SkeletonRows.propTypes = {
  template: PropTypes.string.isRequired,
  count: PropTypes.number.isRequired,
  line: PropTypes.string.isRequired,
};

const Notice = ({ icon, title, body, action, alert }) => {
  const theme = useTheme();
  const t = tokens(theme);
  return (
    <div
      role={alert ? 'alert' : undefined}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
        borderTop: '1px solid ' + t.line,
        padding: '48px 20px',
        textAlign: 'center',
        fontSize: 14,
        color: t.sub,
      }}
    >
      <Icon name={icon} size={26} color={inkA(theme, 0.3)} />
      <span style={{ fontWeight: 700, color: t.text }}>{title}</span>
      {body ? <span style={{ maxWidth: 360 }}>{body}</span> : null}
      {action}
    </div>
  );
};

Notice.propTypes = {
  icon: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  body: PropTypes.string,
  action: PropTypes.node,
  alert: PropTypes.bool,
};

Notice.defaultProps = { body: null, action: null, alert: false };

const noticeFor = ({ error, onRetry, searching, emptyBody }) => {
  if (error) {
    return (
      <Notice
        alert
        icon="triangle-alert"
        title="Couldn't load entries"
        body={error}
        action={
          onRetry ? (
            <ButtonV2 onClick={onRetry} style={{ width: 'auto', marginTop: 6 }}>
              Retry
            </ButtonV2>
          ) : null
        }
      />
    );
  }
  return searching ? (
    <Notice icon="search" title="No submissions match your search" />
  ) : (
    <Notice icon="inbox" title="No entries yet" body={emptyBody} />
  );
};

const SubmissionsTable = ({
  columns,
  versionIndex,
  rows,
  sort,
  onSort,
  onOpen,
  activeId,
  loading,
  searching,
  emptyBody,
  error,
  onRetry,
  header,
  footer,
}) => {
  const t = tokens(useTheme());
  const cols = columns;
  const template = gridTemplateOf(cols);

  let body = null;
  let notice = null;
  if (error) {
    notice = noticeFor({ error, onRetry });
  } else if (loading && !rows.length) {
    body = (
      <SkeletonRows template={template} count={cols.length} line={t.line} />
    );
  } else if (!rows.length) {
    notice = noticeFor({ searching, emptyBody });
  } else {
    body = rows.map((row, i) => (
      <Row
        key={row.id}
        first={i === 0}
        row={row}
        cols={cols}
        template={template}
        index={versionIndex}
        active={row.id === activeId}
        onOpen={onOpen}
      />
    ));
  }

  return (
    <div
      style={{
        background: t.card,
        border: '1px solid ' + t.border,
        borderRadius: 16,
        boxShadow: t.shadowCard,
        overflow: 'hidden',
      }}
    >
      {header}
      <div style={{ overflowX: 'auto' }}>
        <div
          role="table"
          aria-label="Entries"
          aria-busy={loading ? 'true' : undefined}
          style={{ minWidth: tableMinWidth(cols) }}
        >
          <div role="rowgroup">
            <Grid
              role="row"
              $template={template}
              style={{ background: t.head }}
            >
              {cols.map((col) => (
                <HeaderCell
                  key={col.key}
                  col={col}
                  sort={sort}
                  onSort={onSort}
                />
              ))}
            </Grid>
          </div>
          <div role="rowgroup">{body}</div>
        </div>
      </div>
      {notice}
      {footer}
    </div>
  );
};

SubmissionsTable.propTypes = {
  // The active view's columns (views/viewModel colDef).
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      w: PropTypes.number.isRequired,
      sort: PropTypes.string,
      meta: PropTypes.bool,
      column: PropTypes.object,
      align: PropTypes.string,
    })
  ).isRequired,
  versionIndex: PropTypes.object,
  rows: PropTypes.arrayOf(PropTypes.object),
  sort: PropTypes.shape({ key: PropTypes.string, direction: PropTypes.string }),
  onSort: PropTypes.func.isRequired,
  onOpen: PropTypes.func.isRequired,
  activeId: PropTypes.number,
  loading: PropTypes.bool,
  searching: PropTypes.bool,
  emptyBody: PropTypes.string,
  // The list query's message; replaces the rows with "Couldn't load entries".
  error: PropTypes.string,
  onRetry: PropTypes.func,
  header: PropTypes.node,
  footer: PropTypes.node,
};

SubmissionsTable.defaultProps = {
  versionIndex: {},
  rows: [],
  sort: DEFAULT_SORT,
  activeId: null,
  loading: false,
  searching: false,
  emptyBody: null,
  error: null,
  onRetry: undefined,
  header: null,
  footer: null,
};

export default SubmissionsTable;
